import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { getRefundCustomerAttConf, getRefundDetails, getRefundLinerAttConf } from './RefundActions';

/** Same shape as `IDemurrageDocAttachmentList` / `attData` in NewBlRequestSlice */
interface IRefundDocAttachmentList {
    list: any[];
    next: string | null;
    previous: string | null;
}

/**
 * Customer block: `customerAttData`, `customerDocList`, … (mirrors liner: `linerAttData`, `linerDocList`, …)
 * Liner block: NBRL-style `selectedLiner*` + `linerAttachmentId` + liner att grid
 */
export interface IRefundState {
    dataLoading: boolean;

    refundFormView: {
        refundRequestId: any;
        blId: any;
        requestedPartnerId: any;
        status: any;
        statusLabel: string;
        groupLevel: number;
        attList: any[];
    };

    // ══ Customer documents (prefixed — parallel to liner* naming) ══
    customerAttData: IRefundDocAttachmentList;
    customerDocList: any[] | null;
    customerDropdownDocList: any[] | null;
    selectedCustomerAttachmentName: any;
    selectedCustomerAttachmentBytrr: any;
    selectedCustomerAttachmentFilePath: any;
    selectedCustomerDocId: any;
    selectedCustomerFileServerPath: any;

    // ══ Liner — same as NewBlRequestSlice liner fields ══
    selectedLinerAttachmentName: any;
    selectedLinerAttachmentFilePath: any;
    selectedLinerDocId: any;
    selectedLinerFileServerPath: any;
    linerAttachmentId: any;

    // ══ Liner att grid ══
    linerAttData: IRefundDocAttachmentList;
    linerDocList: any[] | null;
    linerDropdownDocList: any[] | null;
}

const initialState: IRefundState = {
    dataLoading: false,

    refundFormView: {
        refundRequestId: null,
        blId: null,
        requestedPartnerId: null,
        status: null,
        statusLabel: '',
        groupLevel: 0,
        attList: [],
    },

    customerAttData: {
        list: [],
        next: null,
        previous: null,
    },
    customerDocList: null,
    customerDropdownDocList: null,
    selectedCustomerAttachmentName: {},
    selectedCustomerAttachmentBytrr: {},
    selectedCustomerDocId: {},
    selectedCustomerAttachmentFilePath: {},
    selectedCustomerFileServerPath: {},

    selectedLinerAttachmentFilePath: {},
    selectedLinerAttachmentName: {},
    selectedLinerDocId: {},
    /** Per-row map like `selectedCustomerFileServerPath` (not an array) */
    selectedLinerFileServerPath: {} as any,
    linerAttachmentId: null,

    linerAttData: {
        list: [],
        next: null,
        previous: null,
    },
    linerDocList: null,
    linerDropdownDocList: null,
};

function mapAttConfPayload(payload: any[]) {
    const updatedDocList =
        (payload
            ?.filter((i: any) => i?.isMandatory != 'H' && i?.isMandatory != 'E')
            ?.map((i: any) => ({
                rowNumber: crypto.randomUUID(),
                id: i?.docId,
                name: i?.docName,
                m: i?.isMandatory == 'Y',
            })) as any[]) ?? [];

    const dropdownOptions =
        (payload
            ?.filter((i: any) => i?.isMandatory == 'H')
            ?.map((i: any) => ({
                id: i?.docId,
                name: i?.docName,
                m: true,
                isDropDownDoc: true,
            })) as any[]) ?? [];

    const attList = (updatedDocList ?? []).map((x: any) => ({
        rowNumber: x.rowNumber,
        attachmentId: null,
        docId: x.id,
        fileName: '',
        verify: false,
        actionBy: '',
        actionOn: '',
        m: x.m,
        ...(x.hasOwnProperty('isDropDownDoc') ? { isDropDownDoc: x.isDropDownDoc } : {}),
    }));

    return { updatedDocList, dropdownOptions, attList };
}

export const RefundSlice = createSlice({
    name: 'refund',
    initialState,
    reducers: {
        // ── Customer  ──

        updateSelectedCustomerAttachmentName: (state, action: PayloadAction<any>) => {
            state.selectedCustomerAttachmentName = action.payload;
        },

        updateSelectedCustomerAttachmentBytrr: (state, action: PayloadAction<any>) => {
            state.selectedCustomerAttachmentBytrr = action.payload;
        },

        updateSelectedCustomerDocId: (state, action: PayloadAction<any>) => {
            state.selectedCustomerDocId = action.payload;
        },

        updateCustomerAttData: (state, action: PayloadAction<any>) => {
            state.customerAttData.list = action.payload;
        },

        updateSelectedCustomerAttachmentFilePath: (state, action: PayloadAction<any>) => {
            state.selectedCustomerAttachmentFilePath = action.payload;
        },

        updateSelectedCustomerFileServerPath: (state, action: PayloadAction<any>) => {
            state.selectedCustomerFileServerPath = action.payload;
        },

        clearSelectedCustomerFileServerPath: (state) => {
            state.selectedCustomerFileServerPath = {};
        },

        clearCustomerFields: (state) => {
            state.customerDropdownDocList = null;
            state.selectedCustomerAttachmentName = {};
            state.selectedCustomerAttachmentFilePath = {};
            state.selectedCustomerFileServerPath = {};
        },
        clearRefundViewAndCustomerState: (state) => {
            state.refundFormView = {
                refundRequestId: null,
                blId: null,
                requestedPartnerId: null,
                status: null,
                statusLabel: '',
                groupLevel: 0,
                attList: [],
            };
            
        },

        addCustomerDocument: (state) => {
            if (!state.customerDropdownDocList || state.customerDropdownDocList.length === 0) return;

            const rowNumber = crypto.randomUUID();
            const newDoc = { rowNumber, ...state.customerDropdownDocList[0] };

            state.customerDocList = [...(state.customerDocList ?? []), newDoc];
            state.customerAttData.list = [
                ...state.customerAttData.list,
                {
                    rowNumber: newDoc.rowNumber,
                    attachmentId: null,
                    docId: newDoc.id,
                    fileName: '',
                    verify: false,
                    actionBy: '',
                    actionOn: '',
                    m: newDoc.m,
                    isDropDownDoc: true,
                },
            ];
        },

        removeCustomerDocument: (state, action: PayloadAction<{ rowNumber: string }>) => {
            const { rowNumber: rowNumberToRemove } = action.payload;

            state.customerDocList = (state.customerDocList ?? []).filter(
                (doc) => doc.rowNumber !== rowNumberToRemove
            );
            state.customerAttData.list = (state.customerAttData.list ?? []).filter(
                (att) => att.rowNumber !== rowNumberToRemove
            );

            const { [rowNumberToRemove]: _rn, ...restName } = state.selectedCustomerAttachmentName ?? {};
            state.selectedCustomerAttachmentName = Object.keys(restName).length > 0 ? restName : {};

            const { [rowNumberToRemove]: _rf, ...restFilePath } = state.selectedCustomerAttachmentFilePath ?? {};
            state.selectedCustomerAttachmentFilePath = Object.keys(restFilePath).length > 0 ? restFilePath : {};

            const { [rowNumberToRemove]: _rs, ...restServerPath } = state.selectedCustomerFileServerPath ?? {};
            state.selectedCustomerFileServerPath = Object.keys(restServerPath).length > 0 ? restServerPath : {};
        },

        updateCustomerListByRowNumber: (
            state,
            action: PayloadAction<{ rowNumber: string; updatedFields: { id: number; name: string } }>
        ) => {
            const { rowNumber, updatedFields } = action.payload;

            const dList = state.customerDocList;
            if (!dList) return;

            const docListIndex = dList.findIndex((doc) => doc.rowNumber === rowNumber);
            if (docListIndex !== -1) {
                dList[docListIndex] = { ...dList[docListIndex], ...updatedFields };
            }

            if (state.customerAttData?.list) {
                const attIndex = state.customerAttData.list.findIndex((att) => att.rowNumber === rowNumber);
                if (attIndex !== -1) {
                    state.customerAttData.list[attIndex] = {
                        ...state.customerAttData.list[attIndex],
                        docId: updatedFields.id,
                    };
                }
            }
        },

        // ── Liner ──

        updateSelectedLinerAttachmentName: (state, action: PayloadAction<any>) => {
            state.selectedLinerAttachmentName = action.payload;
        },

        updateSelectedLinerDocId: (state, action: PayloadAction<any>) => {
            state.selectedLinerDocId = action.payload;
        },

        updateSelectedLinerAttachmentFilePath: (state, action: PayloadAction<any>) => {
            state.selectedLinerAttachmentFilePath = action.payload;
        },

        updateSelectedLinerFileServerPath: (state, action: PayloadAction<any>) => {
            state.selectedLinerFileServerPath = action.payload;
        },

        updateLinerAttachmentId: (state, action: PayloadAction<any>) => {
            state.linerAttachmentId = action.payload;
        },

        updateLinerAdttamentData: (state, action: PayloadAction<any>) => {
            state.linerAttData.list = action.payload;
        },

        /** Clears liner doc grid + per-row map fields (same idea as `clearFields` + grid reset; NBRL `linerAttachmentId` cleared here too). */
        clearLinerGridFields: (state) => {
            state.linerDropdownDocList = null;
            state.linerAttData = { list: [], next: null, previous: null };
            state.linerDocList = null;
            state.selectedLinerDocId = {};
            state.selectedLinerAttachmentFilePath = {};
            state.selectedLinerAttachmentName = {};
            state.selectedLinerFileServerPath = {};
            state.linerAttachmentId = null;
        },

        addLinerDocument: (state) => {
            if (!state.linerDropdownDocList || state.linerDropdownDocList.length === 0) return;

            const rowNumber = crypto.randomUUID();
            const newDoc = { rowNumber, ...state.linerDropdownDocList[0] };

            state.linerDocList = [...(state.linerDocList ?? []), newDoc];
            state.linerAttData.list = [
                ...state.linerAttData.list,
                {
                    rowNumber: newDoc.rowNumber,
                    attachmentId: null,
                    docId: newDoc.id,
                    fileName: '',
                    verify: false,
                    actionBy: '',
                    actionOn: '',
                    m: newDoc.m,
                    isDropDownDoc: true,
                },
            ];
        },

        // Same row-removal pattern as NewBlRequestSlice `removeDocument` (lines 195–215).
        removeLinerDocument: (state, action: PayloadAction<{ rowNumber: string }>) => {
            const { rowNumber: rowNumberToRemove } = action.payload;

            state.linerDocList = (state.linerDocList ?? []).filter((d) => d.rowNumber !== rowNumberToRemove);
            state.linerAttData.list = (state.linerAttData.list ?? []).filter((a) => a.rowNumber !== rowNumberToRemove);

            const { [rowNumberToRemove]: removedLinerName, ...restSelectedLinerAttachmentName } =
                state.selectedLinerAttachmentName ?? {};
            state.selectedLinerAttachmentName = Object.keys(restSelectedLinerAttachmentName).length > 0
                ? restSelectedLinerAttachmentName
                : {};

            const { [rowNumberToRemove]: removedLinerFilePath, ...restSelectedLinerAttachmentFilePath } =
                state.selectedLinerAttachmentFilePath ?? {};
            state.selectedLinerAttachmentFilePath = Object.keys(restSelectedLinerAttachmentFilePath).length > 0
                ? restSelectedLinerAttachmentFilePath
                : {};

            const { [rowNumberToRemove]: removedLinerServerPath, ...restSelectedLinerFileServerPath } =
                state.selectedLinerFileServerPath ?? {};
            state.selectedLinerFileServerPath = Object.keys(restSelectedLinerFileServerPath).length > 0
                ? restSelectedLinerFileServerPath
                : {};
        },

        updateLinerListByRowNumber: (
            state,
            action: PayloadAction<{ rowNumber: string; updatedFields: { id: number; name: string } }>
        ) => {
            const { rowNumber, updatedFields } = action.payload;
            const docList = state.linerDocList;
            if (!docList) return;
            const docIdx = docList.findIndex((d) => d.rowNumber === rowNumber);
            if (docIdx !== -1) docList[docIdx] = { ...docList[docIdx], ...updatedFields };
            const att = state.linerAttData?.list;
            if (att) {
                const i = att.findIndex((a) => a.rowNumber === rowNumber);
                if (i !== -1) {
                    att[i] = { ...att[i], docId: updatedFields.id };
                }
            }
        },
    },

    extraReducers: (builder) => {
        builder.addCase(getRefundDetails.fulfilled, (state, action) => {
            const payload: any = action.payload?.data ?? {};

            state.dataLoading = false;

            // Store lists individually (like DOExtension)
            state.customerDropdownDocList = payload?.dropdownDocList ?? [];
            state.customerDocList = payload?.updatedDocListData ?? [];
            state.customerAttData.list = payload?.attList ?? [];

            state.refundFormView = {
                refundRequestId: payload?.refundRequestId ?? null,
                blId: payload?.blId ?? null,
                requestedPartnerId: payload?.requestedPartnerId ?? null,
                status: payload?.status ?? null,
                statusLabel: payload?.statusLabel ?? '',
                groupLevel: Number(payload?.groupLevel ?? 0),
                attList: payload?.attList ?? [],
            };
        });

        builder.addCase(getRefundCustomerAttConf.fulfilled, (state, action) => {
            const { updatedDocList, dropdownOptions, attList } = mapAttConfPayload(action.payload ?? []);
            state.customerDropdownDocList = dropdownOptions;
            state.customerDocList = updatedDocList;
            state.customerAttData.list = attList;
        });

        builder.addCase(getRefundLinerAttConf.fulfilled, (state, action) => {
            const { updatedDocList, dropdownOptions, attList } = mapAttConfPayload(action.payload ?? []);
            state.linerDropdownDocList = dropdownOptions;
            state.linerDocList = updatedDocList;
            state.linerAttData.list = attList;
        });
    },
});

export const {
    updateSelectedCustomerAttachmentName,
    updateSelectedCustomerAttachmentBytrr,
    updateSelectedCustomerDocId,
    updateCustomerAttData,
    updateSelectedCustomerAttachmentFilePath,
    updateSelectedCustomerFileServerPath,
    clearSelectedCustomerFileServerPath,
    clearCustomerFields,
    clearRefundViewAndCustomerState,
    addCustomerDocument,
    removeCustomerDocument,
    updateCustomerListByRowNumber,
    updateSelectedLinerAttachmentName,
    updateSelectedLinerDocId,
    updateSelectedLinerAttachmentFilePath,
    updateSelectedLinerFileServerPath,
    updateLinerAttachmentId,
    updateLinerAdttamentData,
    clearLinerGridFields,
    addLinerDocument,
    removeLinerDocument,
    updateLinerListByRowNumber,
} = RefundSlice.actions;

export default RefundSlice.reducer;
